import React from 'react';
import { Link } from 'react-router-dom';
import { projects } from '../data/mockData';
import { ArrowUpRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const ProjectsSection = () => {
  const { isDark } = useTheme();
  
  return (
    <section id="projects" className="projects-section">
      {/* Section Title */}
      <h2 className="section-title">
        PAST <span className="section-title-faded">PROJECTS</span>
      </h2>
      <blockquote className="section-quote">
        <p>"The independence of presentational immediacy to contemporary actualities in the environment cannot be exaggerated. It is only by reason of the fortunate dependence of the experient and of these contemporary actualities on a common past, that presentational immediacy is more than a barren aesthetic display."</p>
        <cite>- Alfred North Whitehead (PaR, §324)</cite>
      </blockquote>

      {/* Projects Grid */}
      <div className="projects-grid">
        {projects.map((project) => {
          const isInternalLink = project.link && project.link.startsWith('/');
          const isExternalLink = project.link && !project.link.startsWith('/');
          
          if (isInternalLink) {
            return (
              <Link key={project.id} to={project.link} className="project-card clickable">
                <div className="project-arrow">
                  <ArrowUpRight size={20} />
                </div>
                <div className="project-image-container">
                  <img 
                    src={project.image} 
                    alt={project.title} 
                    className="project-image branded-image"
                  />
                </div>
                <div className="project-details">
                  <h3 className="project-title">{project.title}</h3>
                  <p className="project-category">{project.category}</p>
                </div>
              </Link>
            );
          }
          
          if (isExternalLink) {
            const showArrow = project.title !== 'midpantry';
            return (
              <a key={project.id} href={project.link} target="_blank" rel="noopener noreferrer" className="project-card clickable">
                {showArrow && (
                  <div className="project-arrow">
                    <ArrowUpRight size={20} />
                  </div>
                )}
                <div className="project-image-container">
                  <img 
                    src={project.image} 
                    alt={project.title} 
                    className="project-image branded-image"
                  />
                </div>
                <div className="project-details">
                  <h3 className="project-title">{project.title}</h3>
                  <p className="project-category">{project.category}</p>
                </div>
              </a>
            );
          }
          
          return (
            <div key={project.id} className="project-card">
              {project.image && (
                <div className="project-image-container">
                  <img 
                    src={project.image} 
                    alt={project.title} 
                    className="project-image branded-image"
                  />
                </div>
              )}
              <div className="project-details">
                <h3 className="project-title">{project.title}</h3>
                <p className="project-category">{project.category}</p>
              </div>
            </div>
          );
        })}
      </div>

      <style jsx>{`
        .projects-section {
          padding-bottom: 60px;
        }

        .section-title {
          font-family: 'Poppins', sans-serif;
          font-size: clamp(36px, 6vw, 56px);
          font-weight: 700;
          color: ${isDark ? '#fff' : '#151312'};
          line-height: 1;
          letter-spacing: -0.02em;
          margin: 0 0 32px 0;
          transition: color 0.3s ease;
        }

        .section-title-faded {
          color: ${isDark ? 'rgba(182, 180, 189, 0.2)' : 'rgba(21, 19, 18, 0.15)'};
        }

        .section-quote {
          font-family: 'Poppins', sans-serif;
          font-size: 15px;
          font-style: italic;
          color: ${isDark ? '#998f8f' : '#64748b'};
          line-height: 1.6;
          margin: -12px 0 32px 0;
          max-width: 600px;
          padding-left: 16px;
          border-left: 2px solid rgba(244, 108, 56, 0.5);
          transition: color 0.3s ease;
        }

        .section-quote p {
          margin: 0 0 8px 0;
        }

        .section-quote cite {
          font-size: 14px;
          color: #f46c38;
          font-style: normal;
        }

        .projects-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }

        .project-card {
          position: relative;
          border-radius: 16px;
          overflow: hidden;
          text-decoration: none;
          display: block;
          background: transparent;
        }

        .project-card.clickable {
          cursor: pointer;
          transition: transform 0.3s ease;
        }

        .project-card.clickable:hover {
          transform: translateY(-4px);
        }

        .project-card.clickable:hover .project-arrow {
          opacity: 1;
          transform: rotate(-45deg) scale(1);
        }

        .project-card.clickable:hover .project-image {
          transform: scale(1.05);
        }

        .project-arrow {
          position: absolute;
          top: 16px;
          right: 16px;
          width: 36px;
          height: 36px;
          background: transparent;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #f46c38;
          z-index: 2;
          opacity: 0;
          transform: rotate(-45deg) scale(0.8);
          transition: all 0.3s ease;
        }

        .project-image-container {
          aspect-ratio: 5/6;
          overflow: hidden;
          border-radius: 8px;
        }

        .project-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .project-image.branded-image {
          object-fit: contain;
          object-position: center;
          background: transparent;
          padding: 0;
          transition: background-color 0.3s ease;
        }

        .project-details {
          padding: 16px 0;
          text-align: center;
        }

        .project-title {
          font-family: 'Poppins', sans-serif;
          font-size: 18px;
          font-weight: 600;
          color: ${isDark ? '#fff' : '#151312'};
          margin: 0 0 4px 0;
          transition: color 0.3s ease;
        }

        .project-category {
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          font-weight: 400;
          color: ${isDark ? '#998f8f' : '#64748b'};
          margin: 0;
          transition: color 0.3s ease;
        }

        @media (max-width: 1200px) {
          .projects-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 600px) {
          .projects-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }

          .section-title {
            font-size: 32px;
            margin-bottom: 24px;
          }

          .project-title {
            font-size: 14px;
          }

          .project-category {
            font-size: 11px;
          }

          .project-details {
            padding: 8px 0;
          }
        }
      `}</style>
    </section>
  );
};

export default ProjectsSection;
